import { ChevronDownIcon } from '@chakra-ui/icons';
import { type ForwardedRef, forwardRef, useEffect } from 'react';
import { FaCog } from 'react-icons/fa';
import { VscCircleFilled } from 'react-icons/vsc';
import { FadeInOut } from '@/components/FadeInOut';
import { useOpenSettingsModal } from '@/components/SettingsModal';
import { useCollection } from '@/features/events/contexts/CollectionContext/useCollection';
import { usePickerIntegrations } from '@/features/interactions/addToCalendar/hooks/usePickerIntegrations';
import type { Destination } from '@/features/interactions/addToCalendar/types';
import { PlatformTypes } from '@/types/gql.generated';

import {
  Flex,
  Menu,
  MenuButton,
  MenuGroup,
  MenuItem,
  MenuList,
  Text,
  Icon,
  useDisclosure,
  type UseDisclosureProps,
  type ButtonProps,
  IconButton,
  Portal,
  Spinner,
} from '@/ui';
import { isValidEmail } from '@/utils/validation';
import { useInteractionMenuLevel } from '../../lib/interactionMenuLevel';
import { isSuitableIntegration } from '../calendar.lib';
import { useCalendarAuth } from '../hooks/useCalendarAuth';
import { BasicGoogleCalendarButton } from './BasicGoogleCalendarButton';
import { IntegrationError } from './IntegrationErrors/IntegrationError';

type Props = Omit<ButtonProps, 'onChange' | 'value'> & {
  value: Destination | null;
  onChange: (choice: Destination) => void;
};

const Picker = (
  { onChange, value, ...props }: Props,
  ref: ForwardedRef<UseDisclosureProps>
) => {
  const { openSettingsModal } = useOpenSettingsModal();
  const menuLevel = useInteractionMenuLevel();
  const { integrations, isLoading } = usePickerIntegrations({
    platform: PlatformTypes.GoogleIntegration,
  });
  const { base } = useCollection();
  const { handleCalendarAuth } = useCalendarAuth({ base: base ?? undefined });
  const suitableIntegrations = integrations.filter((item) =>
    isSuitableIntegration(item.integration)
  );

  const disclosure = useDisclosure();
  const { isOpen, onClose, onOpen } = disclosure;

  useEffect(() => {
    if (typeof ref === 'function') {
      ref(disclosure);
    } else if (ref) {
      ref.current = disclosure;
    }
  }, [ref, disclosure]);

  if (!isLoading && suitableIntegrations.length === 0) {
    return null;
  }

  return (
    <Menu isOpen={isOpen} matchWidth onClose={onClose} onOpen={onOpen}>
      <FadeInOut key={String(isLoading)} w="100%">
        {isLoading ? (
          <Flex align="center" flex="1" gap="2">
            <Spinner size="sm" />
            Loading calendars
          </Flex>
        ) : (
          <MenuButton
            _hover={{ borderColor: 'gray.300' }}
            bg="white"
            borderRadius="md"
            borderWidth="1px"
            flexGrow="1"
            h="10"
            overflow="hidden" // required for text overflow to work
            pl="3"
            pr="2"
            type="button"
            {...props}
          >
            <Flex align="center" as="span" gap="4" justify="space-between">
              <Flex
                align="center"
                as="span"
                flexShrink="1"
                gap="3"
                justify="flex-start"
                minW="0"
              >
                {value && (
                  <Icon
                    as={VscCircleFilled}
                    boxSize="20px"
                    color={value.calendar.color}
                  />
                )}
                <Text
                  as="span"
                  fontWeight={value ? 'normal' : 'bold'}
                  overflow="hidden"
                  textOverflow="ellipsis"
                  whiteSpace="nowrap"
                >
                  {value ? value.calendar.name : 'Choose a calendar'}
                </Text>
              </Flex>
              <ChevronDownIcon boxSize="20px" />
            </Flex>
          </MenuButton>
        )}
      </FadeInOut>
      {/* Portalled to the body, so the menu leaves this form's stacking context
          and competes with page chrome directly. useInteractionMenuLevel is
          what keeps it above a modal the form is running inside, and below a
          sticky surface that has to stay on top of it. */}
      <Portal>
        <MenuList
          data-control
          maxH="16.9rem"
          overflowX="hidden"
          overflowY="auto"
          zIndex={menuLevel}
        >
          {integrations.map(({ integration, suitableCalendars }) => {
            const isSuitable = isSuitableIntegration(integration);
            return (
              <MenuGroup
                fontWeight="normal"
                key={integration.id}
                opacity="0.6"
                title={integration.name}
              >
                {suitableCalendars.map((calendar) => {
                  return (
                    <MenuItem
                      isDisabled={calendar.readonly || !isSuitable}
                      key={calendar.uri}
                      icon={
                        <Icon
                          as={VscCircleFilled}
                          boxSize="20px"
                          color={calendar.color}
                        />
                      }
                      onClick={() =>
                        onChange({
                          calendar,
                          integration,
                          platform: PlatformTypes.GoogleIntegration,
                        })
                      }
                    >
                      {/* minW=0 down the flex chain lets the name shrink and
                          ellipsize instead of stretching the menu sideways. */}
                      <Text
                        alignItems="center"
                        as="span"
                        display="flex"
                        fontWeight="medium"
                        gap="2"
                        minW="0"
                        pointerEvents="auto"
                      >
                        <Text
                          as="span"
                          minW="0"
                          overflow="hidden"
                          textOverflow="ellipsis"
                          whiteSpace="nowrap"
                        >
                          {calendar.name}
                        </Text>
                      </Text>
                    </MenuItem>
                  );
                })}
                {/* usableIntegrations guarantees a non-empty suitableCalendars,
                    so a missing write grant is the one error left to surface. */}
                {!isSuitable ? (
                  <IntegrationError integration={integration} />
                ) : (
                  <Flex
                    borderTop="1px solid"
                    color="gray.200"
                    justify="flex-end"
                    pb="1"
                    pt="3"
                    px="2"
                  >
                    <BasicGoogleCalendarButton
                      message="Connect another Google Calendar"
                      onClick={() =>
                        handleCalendarAuth({
                          email: isValidEmail(integration.externalId)
                            ? integration.externalId
                            : undefined,
                        })
                      }
                    />
                  </Flex>
                )}
              </MenuGroup>
            );
          })}
          <IconButton
            aria-label="Settings"
            color="gray.600"
            colorScheme="dark"
            icon={<Icon as={FaCog} />}
            ml="auto"
            pos="absolute"
            right="1.5"
            size="xs"
            top="2"
            variant="ghost"
            onClick={() => openSettingsModal('calendars')}
          />
        </MenuList>
      </Portal>
    </Menu>
  );
};

export const GoogleCalendarPicker = forwardRef(Picker);
